import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, Package, X, CheckCircle, Trash2, Truck } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/book-in.css";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playComplete,
} from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import { useDemoVisible } from "../components/helpers/demoMode";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import { useProgressTimer, ProgressBar, useSessionScore, ScoreDisplay } from "../components/gamification";
import { apiFetch } from "../api/apiFetch";

const DEFAULT_DURATION_MS = 4000;
const KEYBOARD_BONUS_MS = 20000;
const ERROR_PAUSE_MS = 2000;

// Stable toast IDs so rapid repeated errors dedupe instead of queueing.
// react-toastify's `limit={1}` queues extras; matching toastId turns extras
// into no-ops while the first is still on screen.
const TOAST_ID = {
    nothingScanned: "bi-nothing-scanned",
    invalidTote: "bi-invalid-tote",
    invalidScan: "bi-invalid-scan",
    itemNotInTote: "bi-item-not-in-tote",
    serverError: "bi-server-error",
    toteEmpty: "bi-tote-empty",
    alreadyInTote: "bi-already-in-tote",
    toteInUse: "bi-tote-in-use",
    toteResumed: "bi-tote-resumed",
    userHasOpen: "bi-user-has-open",
    toteStale: "bi-tote-stale",
    ppdFailed: "bi-ppd-failed",
    cannotReopen: "bi-cannot-reopen",
    holdsPickedStock: "bi-holds-picked-stock",
    noOpenManifest: "bi-no-open-manifest",
    alreadyInStock: "bi-already-in-stock",
    manifestShort: "bi-manifest-short",
    colourMismatch: "bi-colour-mismatch",
    poMismatch: "bi-po-mismatch",
} as const;

// select-supplier / select-po are the two extra pick steps behind the
// Choose Supplier tile; the scanner is ignored on all three pick steps.
type Phase = "select-category" | "select-supplier" | "select-po" | "awaiting-tote" | "tote-open" | "confirming-close";
const isPickStep = (p: Phase) => p === "select-category" || p === "select-supplier" || p === "select-po";

// Sticky PO: the supplier + PO the operator last booked against, kept
// across page visits (Complete & Putaway leaves the page) so they land back
// on scan-tote for the same PO until they press Change.
const PO_STORAGE_KEY = "book-in:po";
const persistPo = (supplier: string | null, po: string | null) => {
    try { window.localStorage.setItem(PO_STORAGE_KEY, JSON.stringify({ supplier, po })); } catch { /* ignore */ }
};
const clearPersistedPo = () => {
    try { window.localStorage.removeItem(PO_STORAGE_KEY); } catch { /* ignore */ }
};

// Rows from GET /manifest/po/book-in-suppliers and GET /manifest/po.
interface BookInSupplier {
    supplier_name: string;
    active_pos: number;
    open_lines: number;
}
interface BookInPo {
    po_number: string | null;
    lines: number;
    complete_lines: number;
    expected_qty: number;
    received_qty: number;
}

// First step of book-in: the operator picks the category of what they're
// booking in before scanning a tote. Seven physical tote colours plus two
// non-colour categories (Misc, Supplier). FE-only for now — the selection
// is held in component state and surfaced as a chip once chosen; nothing is
// sent to the backend yet (that wires up in a later pass).
type BookInCategory =
    | "BLUE" | "GREEN" | "GREY" | "ORANGE" | "PINK" | "RED" | "YELLOW"
    | "MISC" | "SUPPLIER";

// Swatch / text pairs per colour. Text flips to near-black on yellow for
// contrast; every other swatch is dark enough for white. labelKey is the
// i18n key so the displayed name still goes through t().
const COLOUR_CATEGORIES: {
    key: BookInCategory;
    labelKey: string;
    swatch: string;
    text: string;
}[] = [
    { key: "BLUE",   labelKey: "colour_blue",   swatch: "#2563eb", text: "#ffffff" },
    { key: "GREEN",  labelKey: "colour_green",  swatch: "#16a34a", text: "#ffffff" },
    { key: "GREY",   labelKey: "colour_grey",   swatch: "#6b7280", text: "#ffffff" },
    { key: "ORANGE", labelKey: "colour_orange", swatch: "#f97316", text: "#ffffff" },
    { key: "PINK",   labelKey: "colour_pink",   swatch: "#ec4899", text: "#ffffff" },
    { key: "RED",    labelKey: "colour_red",    swatch: "#ef4444", text: "#ffffff" },
    { key: "YELLOW", labelKey: "colour_yellow", swatch: "#eab308", text: "#ffffff" },
];

// Map a category key (colour, MISC or SUPPLIER) to its i18n label key, so the
// chip, stats badge, and the colour-mismatch toast all read the same name.
// Returns null for an unknown/absent key.
const categoryLabelKey = (key?: string | null): string | null => {
    if (!key) return null;
    const colour = COLOUR_CATEGORIES.find(c => c.key === key);
    if (colour) return colour.labelKey;
    if (key === "MISC") return "category_misc";
    if (key === "SUPPLIER") return "category_supplier";
    return null;
};

interface BookedItem {
    barcode: string;
    scannedAt?: string;
    po?: string | null;
    pkgId?: string | null;
    // Product title from the catalogue, resolved at scan time. Null for
    // legacy items not in the products table — UI falls back to SKU + barcode.
    title?: string | null;
    qty?: number;
    // Supplier book-in: the operator confirmed this SKU wasn't on the PO
    // and chose to add it. Completion creates a PO line for it.
    offPo?: boolean;
}

// Total units across booked items, not the number of rows. Each row is
// one SKU and may carry a qty > 1 when the warehouse has bulk_qty_book_in
// on, so summing qty (defaulting to 1, the same fallback used everywhere
// a single item is rendered) is what gives the operator the real unit
// count in the tote.
const unitCount = (items: BookedItem[]): number =>
    items.reduce((sum, it) => sum + (typeof it.qty === "number" && it.qty > 0 ? it.qty : 1), 0);

// Server-side persistence means we no longer cache the open tote in
// sessionStorage. On mount the FE always starts at awaiting-tote and the
// user re-scans the tote to resume — checkTote returns mode=resume with the
// items already booked in.

const BookIn = () => {
    usePageTitle("book_in");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const demo = useDemoVisible();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };

    const [phase, setPhase] = useState<Phase>("select-category");
    // The category chosen on the new first step. null until the operator
    // picks one; cleared again whenever a tote cycle finishes so the next
    // tote starts from a fresh category choice.
    const [category, setCategory] = useState<BookInCategory | null>(null);
    // Supplier book-in: the supplier + PO chosen after Choose Supplier.
    // Both null in colour / Misc mode. Sent with every tote / item call so
    // the BE gates scans against the PO and consumes only its lines.
    const [supplier, setSupplier] = useState<string | null>(null);
    const [poNumber, setPoNumber] = useState<string | null>(null);
    const [supplierList, setSupplierList] = useState<BookInSupplier[]>([]);
    const [poList, setPoList] = useState<BookInPo[]>([]);
    const [listLoading, setListLoading] = useState(false);
    // "Not on this PO — add it?" prompt for an off-PO scan. Holds the scan
    // so Add can commit it with offPo=true; Cancel drops it.
    const [notOnPoPrompt, setNotOnPoPrompt] = useState<
        | { barcode: string; pkgId: string | null; title: string | null }
        | null
    >(null);
    const [addingOffPo, setAddingOffPo] = useState(false);
    // Last scanned SKU's received/expected on the PO, shown in the stats bar.
    const [poProgress, setPoProgress] = useState<{ received: number; expected: number } | null>(null);
    const [toteId, setToteId] = useState<string | null>(null);
    const [items, setItemsState] = useState<BookedItem[]>([]);
    const [scanValue, setScanValue] = useState("");
    // Scan in-flight flag. Only written today (the scan input isn't gated on
    // it — the burst queue handles overlap), kept so a future spinner can
    // read it without re-plumbing doSubmit.
    const [, setLoading] = useState(false);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showAbandonModal, setShowAbandonModal] = useState(false);
    const [abandoning, setAbandoning] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [removeMode, setRemoveMode] = useState(false);

    // Pending in-progress tote surfaced on mount as a hint. Used to be
    // auto-opened, but a stale row (forgotten test tote, abandoned shift)
    // would drop the operator straight into the wrong context — and any
    // subsequent action (close + put-away) would treat its leftover items
    // as real. Now the operator must explicitly scan the tote to confirm
    // resume; only the hint is shown until then.
    const [pendingResume, setPendingResume] = useState<
        | { toteId: string; itemCount: number }
        | null
    >(null);

    // Reopen modal — shown when checkTote returns reason="locked" (the tote
    // is in 'closed' state with completed items). Lets the user choose
    // continue-from-current vs empty + restart, or cancel.
    const [reopenPrompt, setReopenPrompt] = useState<
        | { toteId: string; items: BookedItem[]; completedByName: string | null }
        | null
    >(null);
    const [reopening, setReopening] = useState(false);
    // Destructive-confirm state — when true the reopen modal swaps to the
    // "are you sure you want to empty?" body. Reset whenever the modal
    // closes so the next open starts at the choice screen.
    const [confirmingEmpty, setConfirmingEmpty] = useState(false);

    // Join modal — shown when checkTote returns mode="resume" but the
    // in-progress row was started by a DIFFERENT user (only possible when
    // the warehouse has allow_multi_user_book_in enabled). Forces the
    // joining user to acknowledge they're augmenting a teammate's tote.
    const [joinPrompt, setJoinPrompt] = useState<
        | { toteId: string; items: BookedItem[]; startedByName: string | null }
        | null
    >(null);
    // Warehouse setting — when false, the same barcode can't be scanned into a
    // tote twice. Default to false (stricter) until the real setting loads.
    const [allowDuplicates, setAllowDuplicates] = useState(false);
    const allowDuplicatesRef = useRef(allowDuplicates);
    useEffect(() => { allowDuplicatesRef.current = allowDuplicates; }, [allowDuplicates]);

    // Warehouse setting — when on, every successful item scan opens a
    // small modal asking how many of that SKU went into the tote. Lets
    // one scan stand in for many units. Default off.
    const [bulkQty, setBulkQty] = useState(false);
    const bulkQtyRef = useRef(bulkQty);
    useEffect(() => { bulkQtyRef.current = bulkQty; }, [bulkQty]);

    // Per-row qty editor. Operator clicks the qty chip on an existing
    // book_in row to bump that row's quantity from 1 to whatever they
    // physically put in the tote. The chip is only rendered when
    // bulk_qty_book_in is on; off-warehouses see a plain row.
    const [editingItem, setEditingItem] = useState<BookedItem | null>(null);
    const [editingQtyValue, setEditingQtyValue] = useState<string>("1");
    const [editingQtySubmitting, setEditingQtySubmitting] = useState(false);

    // Per-PO timer targets (ms). Missing/empty entries fall back to DEFAULT_DURATION_MS.
    const poTimersRef = useRef<Record<string, number>>({});

    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Refs for stale state inside closures
    const phaseRef = useRef(phase);
    const toteIdRef = useRef(toteId);
    const itemsRef = useRef(items);
    const categoryRef = useRef(category);
    const showCloseModalRef = useRef(showCloseModal);
    const removeModeRef = useRef(removeMode);
    const reopenPromptRef = useRef(reopenPrompt);
    const confirmingEmptyRef = useRef(confirmingEmpty);

    // Scan-burst queue. A fast scanner can fire the next barcode before
    // the server has answered the previous one — without a queue those
    // extras get dropped (the keydown listener short-circuits while the
    // input is `disabled`). Stash overflow here and drain in finally.
    const inFlightRef = useRef(false);
    const scanQueueRef = useRef<string[]>([]);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { toteIdRef.current = toteId; }, [toteId]);
    useEffect(() => { itemsRef.current = items; }, [items]);
    // Every items write goes through here so itemsRef is current the
    // moment the list changes, not after React's next commit. The
    // duplicate gate in doSubmit reads itemsRef, and a queued scan
    // (scanner double-read that landed mid-save) is replayed on a
    // setTimeout(0) that can run before the passive effect above — on
    // 2026-10-19 that window let five repeat scans through in Poland.
    const setItems = useCallback((next: BookedItem[]) => {
        itemsRef.current = next;
        setItemsState(next);
    }, []);
    useEffect(() => { categoryRef.current = category; }, [category]);
    const supplierRef = useRef(supplier);
    const poRef = useRef(poNumber);
    useEffect(() => { supplierRef.current = supplier; }, [supplier]);
    useEffect(() => { poRef.current = poNumber; }, [poNumber]);
    const notOnPoRef = useRef(notOnPoPrompt);
    useEffect(() => { notOnPoRef.current = notOnPoPrompt; }, [notOnPoPrompt]);

    // Restore a sticky PO from a previous visit (set when the operator
    // picks a PO, cleared by Change) and land straight on scan-tote.
    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(PO_STORAGE_KEY);
            if (!raw) return;
            const saved = JSON.parse(raw);
            if (saved && typeof saved === "object" && (saved.supplier || saved.po)) {
                setSupplier(typeof saved.supplier === "string" ? saved.supplier : null);
                setPoNumber(typeof saved.po === "string" ? saved.po : null);
                setCategory("SUPPLIER");
                setPhase("awaiting-tote");
            }
        } catch { /* corrupt entry — start from the picker */ }
    }, []);
    useEffect(() => { showCloseModalRef.current = showCloseModal; }, [showCloseModal]);
    useEffect(() => { removeModeRef.current = removeMode; }, [removeMode]);
    useEffect(() => { reopenPromptRef.current = reopenPrompt; }, [reopenPrompt]);
    useEffect(() => { confirmingEmptyRef.current = confirmingEmpty; }, [confirmingEmpty]);

    // Fetch the warehouse's duplicate-barcode setting on mount.
    useEffect(() => {
        const warehouse = userObject.warehouse;
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(warehouse)}`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (typeof data.allow_duplicate_barcodes === "boolean") {
                    setAllowDuplicates(data.allow_duplicate_barcodes);
                }
                if (typeof data.bulk_qty_book_in === "boolean") {
                    setBulkQty(data.bulk_qty_book_in);
                }
                if (data.book_in_po_timers && typeof data.book_in_po_timers === "object") {
                    poTimersRef.current = data.book_in_po_timers;
                }
            } catch {
                // Leave default (strict) on failure — safer.
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    // Surface the user's in-progress tote on mount as a hint — but do
    // NOT auto-open it. The operator has to scan the tote to confirm
    // resume. Server-side the tote stays 'open' / book_ins row stays
    // 'in_progress'; on scan, checkTote returns mode='resume' and the
    // standard flow takes over.
    useEffect(() => {
        if (!userObject.uid) return;
        let alive = true;
        (async () => {
            try {
                const params = new URLSearchParams({
                    uid: String(userObject.uid),
                    name: userObject.name ?? "",
                    warehouse: userObject.warehouse ?? "",
                });
                const res = await apiFetch(
                    `/book-in/my-tote?${params.toString()}`,
                    {},
                );
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (!alive || !data.inProgress) return;
                setPendingResume({
                    toteId: data.inProgress.toteId,
                    itemCount: unitCount(data.inProgress.items ?? []),
                });
            } catch {
                // Silently leave the user on awaiting-tote.
            }
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userObject.uid]);

    // Progress bar timer + cumulative session score
    const timer = useProgressTimer();
    const score = useSessionScore();
    const keyboardOpenRef = useRef(false);
    const suspectScanRef = useRef(false);

    // Pause the gamification countdown while the close-confirm modal is
    // shown — the operator is making a decision, not packing, so the
    // progress bar shouldn't tick down. Resumes on cancel; if they
    // confirm Complete the timer is reset by the success handler
    // anyway, so a brief resume-then-reset is harmless.
    useEffect(() => {
        if (showCloseModal) {
            timer.pause();
        } else {
            timer.resume();
        }
    }, [showCloseModal, timer]);

    // Pick the per-PO target from the warehouse settings, else use the default.
    // Still adds the keyboard bonus on top when the virtual keyboard is open.
    const computeNextDurationMs = (po?: string | null) => {
        const custom = po ? poTimersRef.current[po] : undefined;
        const base = typeof custom === "number" && custom > 0 ? custom : DEFAULT_DURATION_MS;
        return base + (keyboardOpenRef.current ? KEYBOARD_BONUS_MS : 0);
    };

    // Abort in-flight fetches on unmount so a late response can't trigger
    // playError() / setState / toast after the page has navigated away.
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    // Hard guard against a double-press re-firing completeTote before the
    // optimistic UI transition takes effect (the modal closes a tick later).
    const completingRef = useRef(false);
    useEffect(() => () => {
        mountedRef.current = false;
        abortRef.current?.abort();
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const flashStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 800);
    };

    // Restart the progress-bar timer after each successful scan.
    // When `awardScore` is true (successful item add), credit the remaining
    // seconds on the current timer window to the session score total.
    // `po` selects the per-PO target for the NEXT window.
    const restartTimer = (awardScore = false, po: string | null = null) => {
        suspectScanRef.current = false;
        if (timer.state.active) {
            const { elapsedMs, completed, durationMs } = timer.stop();
            if (awardScore && !completed && durationMs > 0) {
                const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
                score.addSeconds(secondsLeft, "book_in");
            }
        }
        timer.start(computeNextDurationMs(po));
    };

    const getSignal = () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        return ctrl.signal;
    };

    const completeTote = useCallback(async (opts?: { thenPutaway?: boolean }) => {
        const currentTote = toteIdRef.current;
        const currentItems = itemsRef.current;
        if (!currentTote) return;
        // Re-entrancy guard — block a double-press from firing two completes
        // in the window before the optimistic transition below removes the
        // modal. Reset in `finally` once the request settles.
        if (completingRef.current) return;
        completingRef.current = true;
        setCompleting(true);

        // SUPPLIER is a placeholder — send null so it isn't stored.
        const category = categoryRef.current === "SUPPLIER" ? null : categoryRef.current;
        const thenPutaway = !!opts?.thenPutaway;

        // /book-in/tote/complete runs a synchronous external PPd accept and can
        // take a moment to return. How we hide that latency depends on where
        // the operator is headed next:
        //
        //   • Plain Complete → optimistic. The operator moves on to a
        //     *different* tote, so we transition NOW and let the request finish
        //     in the background. playComplete / playError / toast are global, so
        //     feedback still fires; on failure the BE rolls the tote back to
        //     in_progress so it can be rescanned and retried.
        //
        //   • Complete & Putaway → awaited. Put Away acts on *this same* tote
        //     immediately, and it can't be scanned until the close has actually
        //     persisted (otherwise it reads as still in-use). So we keep the
        //     modal up with a Submitting… state and only navigate once the
        //     server confirms the close.
        if (!thenPutaway) {
            playComplete();
            timer.reset();
            setToteId(null);
            setItems([]);
            // Keep the chosen colour and return to scan-tote so the operator
            // can book the next tote of the same category without re-picking.
            // They use "Change" to switch colour.
            setPhase("awaiting-tote");
            setShowCloseModal(false);
            setRemoveMode(false);
        }

        try {
            const res = await apiFetch(`/book-in/tote/complete`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    toteId: currentTote,
                    items: currentItems,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                    category,
                    supplierName: supplierRef.current,
                    poNumber: poRef.current,
                }),
                // The optimistic plain-Complete request runs unsignalled so a
                // later scan's getSignal() can't tear it down mid-flight. The
                // awaited Putaway request is still on the modal, so a signal
                // lets a manual route-away cancel it cleanly.
                ...(thenPutaway ? { signal: getSignal() } : {}),
            });

            if (res.ok) {
                // Awaited path: the tote is now genuinely closed, so it's safe
                // to send the operator to Put Away to scan it. Hand the tote ID
                // over via navigation state so Put Away auto-claims it and the
                // operator skips re-scanning — they land on the scan
                // item/location step holding the tote they just closed.
                if (thenPutaway && mountedRef.current) {
                    playComplete();
                    timer.reset();
                    setShowCloseModal(false);
                    navigate("/put-away", { state: { autoClaimTote: currentTote } });
                }
                return;
            }

            if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
                return;
            }

            // Backend rolled back; the in_progress row is intact so the
            // operator can rescan the tote and retry. Distinguish the failure
            // modes so the toast points them at the right fix:
            //   ppd_failed        — PPd accept failed; rescan to retry.
            //   manifest_short    — one or more SKUs lack manifest qty; fix
            //                       the manifest, then rescan and retry.
            //   items_missing_sku — a scan couldn't be resolved to a SKU.
            let reason = null;
            let body = null;
            try {
                body = await res.clone().json();
                reason = body?.reason ?? null;
            } catch { /* not JSON / no body */ }
            playError();
            if (reason === "ppd_failed") {
                toast.error(t("ppd_sync_failed"), { toastId: TOAST_ID.ppdFailed });
            } else if (reason === "manifest_short") {
                const list = Array.isArray(body?.shorts)
                    ? body.shorts.map((s: { sku: string; missing: number }) =>
                        `${s.sku} (${s.missing})`).join(", ")
                    : "?";
                toast.error(
                    t("manifest_short", { skus: list }),
                    { toastId: TOAST_ID.manifestShort, autoClose: 8000 },
                );
            } else if (reason === "items_missing_sku") {
                // Should be rare now the BE backfills SKU from barcode.
                // Surface the offending barcodes so the operator can identify
                // which scans the system can't resolve.
                const list = Array.isArray(body?.barcodes)
                    ? body.barcodes.join(", ")
                    : "?";
                toast.error(
                    t("items_missing_sku", { barcodes: list }),
                    { toastId: TOAST_ID.serverError, autoClose: 8000 },
                );
            } else if (reason === "tote_not_in_progress") {
                // A stale Complete: the tote has no open book-in (its last
                // one closed over an hour ago). Nothing was written; the
                // operator scans the tote again to start a fresh one. A
                // repeat within the hour comes back 200 already_completed
                // and lands in the res.ok branch above.
                toast.error(t("book_in_tote_not_in_progress", { tote: currentTote }), { toastId: TOAST_ID.serverError, autoClose: 8000 });
            } else {
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            }
        } catch (err) {
            if ((err as any)?.name === "AbortError") return;
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            completingRef.current = false;
            if (mountedRef.current) setCompleting(false);
        }
    }, [navigate, setItems, t, timer, userObject.uid, userObject.name, userObject.warehouse]);

    // Abandon an empty tote — flips it back to 'available' on the server
    // and resets the page to awaiting-tote. Only invoked from the abandon
    // confirm modal which is itself only opened when items.length === 0.
    const abandonTote = useCallback(async () => {
        const currentTote = toteIdRef.current;
        if (!currentTote) return;
        setAbandoning(true);
        try {
            const res = await apiFetch(`/book-in/tote/abandon`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    toteId: currentTote,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                }),
                signal: getSignal(),
            });
            if (!mountedRef.current) return;
            if (res.ok) {
                playBoxAdd();
                timer.reset();
                setToteId(null);
                setItems([]);
                // Keep the colour — back to scan-tote, not the picker.
                setPhase("awaiting-tote");
                setShowAbandonModal(false);
                setRemoveMode(false);
            } else if (res.status === 401) {
                logoutProcessor();
                navigate("/login");
            } else {
                playError();
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            }
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setAbandoning(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, t, timer, userObject.uid, userObject.name, userObject.warehouse]);

    interface CheckToteResponse {
        valid: boolean;
        mode?: "fresh" | "resume";
        sameUser?: boolean;
        reason?: string;
        items?: BookedItem[];
        currentItems?: BookedItem[];
        startedByName?: string | null;
        completedByName?: string | null;
        openToteId?: string;
        // Set on reason="category_mismatch" — the colour the tote was
        // actually booked in with, so the toast can name it.
        expectedCategory?: string | null;
        // Set on reason="po_mismatch" — the PO the tote is actually
        // booked against (both null = a colour/Misc tote).
        expectedSupplier?: string | null;
        expectedPo?: string | null;
    }

    const checkTote = async (val: string): Promise<CheckToteResponse> => {
        const res = await apiFetch(`/book-in/tote/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: val,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
                // SUPPLIER totes carry no colour — the PO is their identity
                // and the BE gates resume/reopen on it instead.
                category: category === "SUPPLIER" ? null : category,
                supplierName: supplier,
                poNumber,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { valid: false };
        }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    // `reason` is set when valid=false to distinguish "barcode unknown"
    // (no_match) from "barcode known but no open manifest at this
    // warehouse" (no_open_manifest) — the latter gets a more specific
    // toast pointing the operator at the manifest admin.
    const checkItem = async (val: string): Promise<{
        valid: boolean;
        po: string | null;
        pkgId: string | null;
        title: string | null;
        reason?: string;
        // PO mode: the SKU's received/expected across its lines on the PO.
        poLine?: { expected: number; received: number };
        // reason === "already_in_stock": where the SKU already sits in this
        // warehouse — first location by walk order with its qty, plus how
        // many locations hold it in total.
        duplicate?: { location: string; qty: number; locations: number };
    }> => {
        const res = await apiFetch(`/book-in/item/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                barcode: val,
                warehouse: userObject.warehouse,
                supplierName: supplierRef.current,
                poNumber: poRef.current,
                // The duplicate stock gate skips RED / YELLOW totes; the BE
                // reads the colour off the open book-in row by tote id.
                toteId: toteIdRef.current,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { valid: false, po: null, pkgId: null, title: null };
        }
        if (!res.ok) return { valid: false, po: null, pkgId: null, title: null };
        const data = await res.json();
        return {
            valid: !!data.valid,
            po: (data.po ?? null) as string | null,
            // pkgId (= JLI SKU) must round-trip with the item so completeTote
            // can FIFO-allocate manifest qty per SKU. Without this every item
            // arrives at the BE with no SKU and the tote 400s on close.
            pkgId: (data.pkgId ?? null) as string | null,
            title: (data.title ?? null) as string | null,
            reason: typeof data.reason === "string" ? data.reason : undefined,
            poLine: data.poLine && typeof data.poLine === "object"
                ? { expected: Number(data.poLine.expected) || 0, received: Number(data.poLine.received) || 0 }
                : undefined,
            duplicate: data.duplicate && typeof data.duplicate === "object"
                ? {
                    location: String(data.duplicate.location ?? ""),
                    qty: Number(data.duplicate.qty) || 0,
                    locations: Number(data.duplicate.locations) || 1,
                }
                : undefined,
        };
    };

    // Result envelope so the caller can distinguish "tote no longer in
    // progress" (409 — closed by another operator / different tab) from a
    // generic transport failure. Mutations like add/remove return both
    // statuses but the FE wants to react differently to each.
    type ItemMutationResult =
        | { kind: "ok"; items: BookedItem[] }
        // Server-side duplicate gate (allow_duplicate_barcodes off and the
        // barcode is already on the tote). Carries the server's list so the
        // page can resync whatever copy let the scan through.
        | { kind: "duplicate"; items: BookedItem[] }
        | { kind: "stale" }
        | { kind: "error" };

    const addItemRemote = async (item: BookedItem): Promise<ItemMutationResult> => {
        const res = await apiFetch(`/book-in/tote/add-item`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: toteIdRef.current,
                item,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { kind: "error" };
        }
        if (res.status === 409) {
            const body = await res.json().catch(() => null);
            if (body?.reason === "already_in_tote") {
                return {
                    kind: "duplicate",
                    items: Array.isArray(body.items) ? body.items : itemsRef.current,
                };
            }
            return { kind: "stale" };
        }
        if (!res.ok) return { kind: "error" };
        const data = await res.json();
        return Array.isArray(data.items)
            ? { kind: "ok", items: data.items }
            : { kind: "error" };
    };

    const removeItemRemote = async (barcode: string): Promise<ItemMutationResult> => {
        const res = await apiFetch(`/book-in/tote/remove-item`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: toteIdRef.current,
                barcode,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { kind: "error" };
        }
        if (res.status === 409) return { kind: "stale" };
        if (!res.ok) return { kind: "error" };
        const data = await res.json();
        return Array.isArray(data.items)
            ? { kind: "ok", items: data.items }
            : { kind: "error" };
    };

    // Tote we thought was open isn't in progress anymore (closed by another
    // operator or a sibling tab). Bail to awaiting-tote and tell the user.
    const handleStaleTote = () => {
        playError();
        flashStatus("error");
        toast.error(
            t("tote_closed_externally", { tote: toteIdRef.current ?? "?" }),
            { toastId: TOAST_ID.toteStale },
        );
        timer.reset();
        setToteId(null);
        setItems([]);
        // Keep the colour — back to scan-tote, not the picker.
        setPhase("awaiting-tote");
        setRemoveMode(false);
    };

    // Reopen a closed tote. mode="continue" carries the previous items;
    // mode="empty" starts fresh. Caller is responsible for UI transitions.
    const reopenToteRemote = async (
        targetToteId: string,
        mode: "continue" | "empty",
    ): Promise<BookedItem[] | null | "refused"> => {
        const res = await apiFetch(`/book-in/tote/reopen`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: targetToteId,
                mode,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
                // SUPPLIER totes carry no colour; the PO identifies them.
                category: category === "SUPPLIER" ? null : category,
                supplierName: supplier,
                poNumber,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return null;
        }
        if (!res.ok) {
            // The server re-checks the picked-stock gate under its row
            // lock; surface that specific refusal instead of a generic
            // server error (the tote was closed by Drop/Picking between
            // the scan and the reopen choice).
            const body = await res.json().catch(() => null);
            if (body?.reason === "holds_picked_stock") {
                toast.error(
                    t("tote_holds_picked_stock"),
                    { toastId: TOAST_ID.holdsPickedStock, autoClose: 8000 },
                );
                setReopenPrompt(null);
                setConfirmingEmpty(false);
                return "refused";
            }
            return null;
        }
        const data = await res.json();
        return Array.isArray(data.items) ? data.items : [];
    };

    const handleReopenChoice = async (mode: "continue" | "empty") => {
        const target = reopenPrompt?.toteId;
        if (!target) return;
        setReopening(true);
        try {
            const result = await reopenToteRemote(target, mode);
            if (!mountedRef.current) return;
            if (result === "refused") {
                playError();
                return;
            }
            if (!result) {
                playError();
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            setToteId(target);
            setItems(result);
            setPhase("tote-open");
            setReopenPrompt(null);
            setConfirmingEmpty(false);
            setPendingResume(null);
            playBoxAdd();
            flashStatus("success");
            timer.start(computeNextDurationMs());
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setReopening(false);
        }
    };

    const cancelReopen = () => {
        setReopenPrompt(null);
        setConfirmingEmpty(false);
    };

    const acceptJoin = () => {
        const target = joinPrompt;
        if (!target) return;
        setToteId(target.toteId);
        setItems(target.items);
        setPhase("tote-open");
        setJoinPrompt(null);
        setPendingResume(null);
        playBoxAdd();
        flashStatus("success");
        timer.start(computeNextDurationMs());
    };

    const cancelJoin = () => setJoinPrompt(null);

    // Lost-tote escape hatch from the awaiting-tote phase. The page
    // shows a "Resume {tote} (N) — scan to open" hint when the operator
    // returns to a session with an in-progress tote; if the physical
    // barcode is gone, this loads the in-progress row directly and
    // opens the abandon modal (empty) or close modal (has items).
    const resumeAndClose = async () => {
        const target = pendingResume;
        if (!target) return;
        try {
            const params = new URLSearchParams({
                uid: String(userObject.uid ?? ""),
                name: userObject.name ?? "",
                warehouse: userObject.warehouse ?? "",
            });
            const resMine = await apiFetch(
                `/book-in/my-tote?${params.toString()}`,
                {},
            );
            const dataMine = resMine.ok ? await resMine.json() : null;
            if (!mountedRef.current) return;
            const restored: BookedItem[] = dataMine?.inProgress?.items ?? [];
            setToteId(target.toteId);
            setItems(restored);
            // Sync the chip to the resumed tote's real colour / PO.
            applyInProgressContext(dataMine?.inProgress);
            setPhase("tote-open");
            setPendingResume(null);
            if (restored.length === 0) {
                setShowAbandonModal(true);
            } else {
                setShowCloseModal(true);
            }
            playBoxAdd();
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        }
    };

    const openItemQtyEditor = (item: BookedItem) => {
        setEditingItem(item);
        setEditingQtyValue(String(item.qty ?? 1));
        timer.pause();
    };

    const closeItemQtyEditor = () => {
        setEditingItem(null);
        setEditingQtyValue("1");
        setEditingQtySubmitting(false);
        timer.resume();
    };

    const updateItemQtyRemote = async (
        item: BookedItem,
        qty: number,
    ): Promise<ItemMutationResult> => {
        const res = await apiFetch(`/book-in/tote/update-item-qty`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: toteIdRef.current,
                barcode: item.barcode,
                scannedAt: item.scannedAt,
                qty,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { kind: "error" };
        }
        if (res.status === 409) return { kind: "stale" };
        if (!res.ok) return { kind: "error" };
        const data = await res.json();
        return Array.isArray(data.items)
            ? { kind: "ok", items: data.items }
            : { kind: "error" };
    };

    // Save the new qty for the currently-edited row. completeTote
    // multiplies manifest consumption by qty when grouping by SKU.
    const submitItemQtyEdit = async () => {
        const target = editingItem;
        if (!target) return;
        const qty = Math.max(1, Math.min(9999, Math.floor(Number(editingQtyValue) || 1)));
        setEditingQtySubmitting(true);
        try {
            const result = await updateItemQtyRemote(target, qty);
            if (!mountedRef.current) return;
            if (result.kind === "stale") {
                handleStaleTote();
                closeItemQtyEditor();
                return;
            }
            if (result.kind === "error") {
                playError();
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                setEditingQtySubmitting(false);
                return;
            }
            setItems(result.items);
            playSuccess();
            closeItemQtyEditor();
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            setEditingQtySubmitting(false);
        }
    };

    // Sync category + PO state to an in-progress row the BE handed back
    // (resume / user-has-open-tote paths), so the chip and every later
    // call reflect what the tote was actually started as.
    const applyInProgressContext = (ip: {
        category?: string | null;
        supplierName?: string | null;
        poNumber?: string | null;
    } | null | undefined) => {
        if (!ip) return;
        if (ip.supplierName || ip.poNumber) {
            setSupplier(ip.supplierName ?? null);
            setPoNumber(ip.poNumber ?? null);
            setCategory("SUPPLIER");
            persistPo(ip.supplierName ?? null, ip.poNumber ?? null);
        } else if (ip.category) {
            setCategory(ip.category as BookInCategory);
        }
    };

    const loadSuppliers = async () => {
        setListLoading(true);
        try {
            const res = await apiFetch(`/manifest/po/book-in-suppliers`, {});
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = res.ok ? await res.json() : null;
            setSupplierList(Array.isArray(data?.suppliers) ? data.suppliers : []);
        } catch {
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setListLoading(false);
        }
    };

    const loadPos = async (name: string) => {
        setListLoading(true);
        try {
            const res = await apiFetch(`/manifest/po?${new URLSearchParams({ supplier: name })}`, {});
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = res.ok ? await res.json() : null;
            setPoList(Array.isArray(data?.pos) ? data.pos : []);
        } catch {
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setListLoading(false);
        }
    };

    // First step: operator taps a category. Colours / Misc go straight to
    // scan-tote; SUPPLIER opens the supplier → PO pick steps first.
    // playBoxAdd is the same neutral confirm tone the rest of the page uses
    // for a successful, non-completing action.
    const selectCategory = (cat: BookInCategory) => {
        setCategory(cat);
        playBoxAdd();
        if (cat === "SUPPLIER") {
            setSupplier(null);
            setPoNumber(null);
            setPhase("select-supplier");
            void loadSuppliers();
            return;
        }
        setPhase("awaiting-tote");
    };

    const selectSupplier = (name: string) => {
        setSupplier(name);
        setPoNumber(null);
        setPhase("select-po");
        playBoxAdd();
        void loadPos(name);
    };

    const selectPo = (po: string | null) => {
        setPoNumber(po);
        persistPo(supplier, po);
        setPoProgress(null);
        setPhase("awaiting-tote");
        playBoxAdd();
    };

    // Re-open the category picker from the scan-tote step (e.g. wrong colour
    // tapped, or moving off a PO). Only reachable before a tote is open, so
    // there's no scanned work to lose. Clears the sticky PO and any
    // pending-resume hint so neither lingers over the picker.
    const backToCategory = () => {
        setCategory(null);
        setSupplier(null);
        setPoNumber(null);
        setPoProgress(null);
        clearPersistedPo();
        setPhase("select-category");
        setPendingResume(null);
    };

    // Translate a category key to its display name (falls back to the raw key
    // if it's somehow unrecognised) — used in the colour-mismatch toast.
    const categoryDisplay = (key?: string | null): string => {
        const labelKey = categoryLabelKey(key);
        return labelKey ? t(labelKey) : String(key ?? "");
    };

    // Shared tail of a successful item scan: persist to the tote, update the
    // list, and play the success feedback. Used by the normal scan path and
    // by the "add to PO" confirm for an off-PO scan.
    const commitScannedItem = async (scanned: BookedItem, po: string | null): Promise<boolean> => {
        const result = await addItemRemote(scanned);
        if (result.kind === "stale") {
            handleStaleTote();
            return false;
        }
        if (result.kind === "duplicate") {
            // The server saw this barcode on the tote already. Same
            // feedback as the client-side gate below, plus a resync so the
            // list matches what the server holds.
            setItems(result.items);
            playError();
            flashStatus("error");
            toast.error(t("already_in_tote"), { toastId: TOAST_ID.alreadyInTote });
            timer.pauseFor(ERROR_PAUSE_MS);
            return false;
        }
        if (result.kind === "error") {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            timer.pauseFor(ERROR_PAUSE_MS);
            return false;
        }
        setItems(result.items);
        playSuccess();
        flashStatus("success");
        restartTimer(true, po);
        return true;
    };

    const confirmAddOffPo = async () => {
        const p = notOnPoPrompt;
        if (!p) return;
        setAddingOffPo(true);
        try {
            await commitScannedItem({
                barcode: p.barcode,
                scannedAt: new Date().toISOString(),
                po: poRef.current,
                pkgId: p.pkgId,
                title: p.title,
                offPo: true,
            }, poRef.current);
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setAddingOffPo(false);
            setNotOnPoPrompt(null);
            timer.resume();
        }
    };

    const cancelNotOnPo = () => {
        setNotOnPoPrompt(null);
        timer.resume();
    };

    const doSubmit = async (scannedValue?: string) => {
        // No category / PO chosen yet — the operator is on a pick step and a
        // stray scanner read shouldn't fall through into tote/item lookup.
        // Likewise while the off-PO prompt is up: the decision is by tap.
        if (isPickStep(phaseRef.current) || notOnPoRef.current) return;

        const val = (scannedValue ?? scanValue).trim();
        if (!val) {
            toast.error(t("nothing_scanned"), { toastId: TOAST_ID.nothingScanned });
            return;
        }

        // Already processing the previous scan — queue this one and
        // let the in-flight call drain it. Without this, fast bursts
        // get dropped while the fetch is in flight.
        if (inFlightRef.current) {
            scanQueueRef.current.push(val);
            return;
        }

        inFlightRef.current = true;
        setLoading(true);

        try {
            // Reopen modal open: scanning the tote again confirms Continue.
            // Disabled while the destructive empty-confirmation is up so a
            // stray scan can't bypass that double-check.
            if (reopenPromptRef.current && !confirmingEmptyRef.current) {
                if (val === reopenPromptRef.current.toteId) {
                    await handleReopenChoice("continue");
                } else {
                    playError();
                    flashStatus("error");
                }
                return;
            }

            // Confirming close: second scan of the tote ID completes
            if (showCloseModalRef.current) {
                if (val === toteIdRef.current) {
                    await completeTote();
                } else {
                    playError();
                    flashStatus("error");
                }
                return;
            }

            if (phaseRef.current === "awaiting-tote") {
                const result = await checkTote(val);
                const { valid, mode, sameUser, reason, items: serverItems, currentItems, startedByName, completedByName, openToteId, expectedCategory, expectedPo } = result;

                // PO gate: the tote is booked against a different PO (or
                // against none, when we're in PO mode / vice versa).
                if (reason === "po_mismatch") {
                    playError();
                    flashStatus("error");
                    toast.error(
                        expectedPo
                            ? t("tote_po_mismatch", { po: expectedPo })
                            : t("tote_po_mismatch_none"),
                        { toastId: TOAST_ID.poMismatch, autoClose: 8000 },
                    );
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                // Colour gate: the tote was booked in (or is in progress)
                // under a different category than the one selected. Tell the
                // operator which colour it actually is and to scan another.
                if (reason === "category_mismatch") {
                    playError();
                    flashStatus("error");
                    toast.error(
                        t("tote_colour_mismatch", { colour: categoryDisplay(expectedCategory) }),
                        { toastId: TOAST_ID.colourMismatch, autoClose: 8000 },
                    );
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                // User already has a different tote open. Auto-redirect them
                // back to it — the existing tote must be closed before they
                // can open a new one.
                if (reason === "user_has_open_tote" && openToteId) {
                    try {
                        const params = new URLSearchParams({
                            uid: String(userObject.uid ?? ""),
                            name: userObject.name ?? "",
                            warehouse: userObject.warehouse ?? "",
                        });
                        const resMine = await apiFetch(
                            `/book-in/my-tote?${params.toString()}`,
                            {},
                        );
                        const dataMine = resMine.ok ? await resMine.json() : null;
                        const restored = dataMine?.inProgress?.items ?? [];
                        setToteId(openToteId);
                        setItems(restored);
                        // Sync the chip to the open tote's real colour / PO.
                        applyInProgressContext(dataMine?.inProgress);
                        setPhase("tote-open");
                        setPendingResume(null);
                        playError();
                        flashStatus("error");
                        toast.info(
                            t("must_close_tote_first", { tote: openToteId }),
                            { toastId: TOAST_ID.userHasOpen },
                        );
                        timer.start(computeNextDurationMs());
                    } catch {
                        playError();
                        flashStatus("error");
                        toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    }
                    return;
                }

                if (valid) {
                    // Resume started by a different operator — pause and ask
                    // before joining their in-progress tote.
                    if (mode === "resume" && sameUser === false) {
                        setJoinPrompt({
                            toteId: val,
                            items: serverItems ?? [],
                            startedByName: startedByName ?? null,
                        });
                        playBoxAdd();
                        flashStatus("success");
                        timer.pauseFor(ERROR_PAUSE_MS);
                        return;
                    }

                    const restored = serverItems ?? [];
                    setToteId(val);
                    setItems(restored);
                    setPhase("tote-open");
                    setPendingResume(null);
                    playBoxAdd();
                    flashStatus("success");
                    timer.start(computeNextDurationMs());
                    if (mode === "resume" && startedByName) {
                        toast.info(
                            t("tote_resumed", { name: startedByName }),
                            { toastId: TOAST_ID.toteResumed },
                        );
                    }
                    return;
                }

                // Tote is locked (closed) — open the reopen modal so the user
                // can choose continue / empty / cancel. This isn't an error;
                // a neutral attention pop + no status-flash is more honest
                // (the modal itself IS the visual feedback). The pause keeps
                // the timer from ticking down while the operator decides.
                if (reason === "locked") {
                    setReopenPrompt({
                        toteId: val,
                        items: currentItems ?? [],
                        completedByName: completedByName ?? null,
                    });
                    playBoxAdd();
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                // Closed tote scanned at a warehouse where allow_tote_reopen
                // is false (typically PPd-synced sites). Tell the operator
                // they can't bring it back to life — the close already
                // committed to PPd and reopening would diverge state.
                if (reason === "cannot_reopen") {
                    playError();
                    flashStatus("error");
                    toast.error(
                        t("cannot_reopen", { name: completedByName ?? "?" }),
                        { toastId: TOAST_ID.cannotReopen, autoClose: 8000 },
                    );
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                // Closed by Shelf/Forecast Drop or Picking — the tote holds
                // picked stock those modules still have to put away / sort.
                // Booking it in "empty" would re-receive those units on top
                // of the rows the claims point at (double count), so the
                // server refuses before any reopen prompt.
                if (reason === "holds_picked_stock") {
                    playError();
                    flashStatus("error");
                    toast.error(
                        t("tote_holds_picked_stock"),
                        { toastId: TOAST_ID.holdsPickedStock, autoClose: 8000 },
                    );
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                // In-use by another operator — show a name-specific toast.
                if (reason === "in_use") {
                    playError();
                    flashStatus("error");
                    toast.error(
                        t("tote_in_use_by", { name: startedByName ?? "?" }),
                        { toastId: TOAST_ID.toteInUse },
                    );
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }

                playError();
                flashStatus("error");
                // Map the backend's status_* reason to a specific message
                // so the operator knows exactly why the tote was rejected.
                const statusMsgs: Record<string, string> = {
                    status_open: t("tote_status_open"),
                    status_closed: t("tote_status_closed"),
                    status_stored: t("tote_status_stored"),
                };
                const msg = (reason && statusMsgs[reason]) || t("invalid_tote");
                const toastId = (reason && statusMsgs[reason])
                    ? `bi-${reason}`
                    : TOAST_ID.invalidTote;
                toast.error(msg, { toastId });
                timer.pauseFor(ERROR_PAUSE_MS);
                return;
            }

            // phase is "tote-open"
            if (val === toteIdRef.current) {
                // Empty tote → confirm-abandon flow. Operator can back out
                // of a tote they opened by mistake; on confirm we mark the
                // book_ins row 'cancelled' and flip the tote back to
                // 'available' so it can be re-used.
                if (itemsRef.current.length === 0) {
                    setShowAbandonModal(true);
                    playBoxAdd();
                    return;
                }
                // Same tote scanned again → open close-confirm modal
                setShowCloseModal(true);
                playBoxAdd();
                return;
            }

            if (removeModeRef.current) {
                // Check locally first so we can short-circuit the API call
                // when the barcode isn't in the tote at all.
                const current = itemsRef.current;
                if (!current.some(i => i.barcode === val)) {
                    playError();
                    flashStatus("error");
                    toast.error(t("item_not_in_tote"), { toastId: TOAST_ID.itemNotInTote });
                    return;
                }
                const result = await removeItemRemote(val);
                if (result.kind === "stale") {
                    handleStaleTote();
                    return;
                }
                if (result.kind === "error") {
                    playError();
                    flashStatus("error");
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    return;
                }
                setItems(result.items);
                playSuccess();
                flashStatus("success");
                return;
            }

            // Warehouses that don't allow duplicates: block a barcode that's
            // already been scanned into this tote before hitting the API.
            // This is the fast path only — add-item enforces the same rule
            // server-side (409 already_in_tote, handled in commitScannedItem)
            // so a scan this check misses still can't double a line.
            if (!allowDuplicatesRef.current && itemsRef.current.some(i => i.barcode === val)) {
                playError();
                flashStatus("error");
                toast.error(t("already_in_tote"), { toastId: TOAST_ID.alreadyInTote });
                timer.pauseFor(ERROR_PAUSE_MS);
                return;
            }

            const { valid, po, pkgId, title, reason, poLine, duplicate } = await checkItem(val);
            // PO mode, SKU not on the PO: ask before adding it. Neutral tone
            // — it's a decision, not an error — and the timer pauses while
            // the operator decides.
            if (!valid && reason === "not_on_po") {
                setNotOnPoPrompt({ barcode: val, pkgId: pkgId ?? null, title: title ?? null });
                playBoxAdd();
                timer.pause();
                return;
            }
            if (!valid) {
                playError();
                flashStatus("error");
                if (reason === "already_in_stock") {
                    // Duplicate stock gate: the SKU is already on a shelf /
                    // in transit here. Name the place so the leader can go
                    // straight to it; longer autoClose because the operator
                    // has to act on it, not just rescan.
                    const vars = {
                        location: duplicate?.location ?? "",
                        qty: String(duplicate?.qty ?? 0),
                        more: String(Math.max((duplicate?.locations ?? 1) - 1, 0)),
                    };
                    toast.error(
                        t((duplicate?.locations ?? 1) > 1 ? "already_in_stock_multi" : "already_in_stock", vars),
                        { toastId: TOAST_ID.alreadyInStock, autoClose: 8000 },
                    );
                } else if (reason === "no_open_manifest") {
                    toast.error(t("no_open_manifest"), { toastId: TOAST_ID.noOpenManifest });
                } else {
                    toast.error(t("invalid_scan"), { toastId: TOAST_ID.invalidScan });
                }
                timer.pauseFor(ERROR_PAUSE_MS);
                return;
            }

            const scanned: BookedItem = {
                barcode: val,
                scannedAt: new Date().toISOString(),
                po: po ?? null,
                pkgId: pkgId ?? null,
                title: title ?? null,
            };
            const ok = await commitScannedItem(scanned, po);
            if (ok && poLine) setPoProgress(poLine);
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            inFlightRef.current = false;
            if (mountedRef.current) {
                setLoading(false);
                setScanValue("");
                // Drain one queued scan if present. setTimeout(0) lets
                // React commit the state updates from this run so the
                // next call reads fresh refs (phase, items, toteId).
                const next = scanQueueRef.current.shift();
                if (next) setTimeout(() => doSubmit(next), 0);
            } else {
                scanQueueRef.current = [];
            }
        }
    };

    const cancelCloseModal = () => {
        setShowCloseModal(false);
    };

    // Command word for centre of screen
    const commandText = phase === "awaiting-tote"
        ? t("scan_tote")
        : showCloseModal
            ? t("scan_to_close")
            : removeMode
                ? t("remove_item")
                : t("scan_item");
    const commandWords = commandText.toUpperCase().split(/[\s-]+/).filter(Boolean);

    // Sub-command hint — smaller text below the big command word so the
    // operator knows what their next move can be (e.g. rescan the tote to
    // close it). Suppressed during the close modal since the modal itself
    // already tells the user what to do. On awaiting-tote, if there's a
    // pending in-progress tote, the hint names it so the operator can
    // make an informed choice rather than scanning into the dark.
    const commandSubText = showCloseModal
        ? null
        : phase === "awaiting-tote"
            ? (pendingResume
                ? t("resume_tote_hint", {
                    tote: pendingResume.toteId,
                    count: String(pendingResume.itemCount),
                })
                : t("scan_tote_sub"))
            : removeMode
                ? t("remove_item_sub")
                : t("scan_item_sub");

    // Display data for the chosen category. Colours carry a swatch; MISC /
    // SUPPLIER have no swatch (rendered with their own icon instead).
    const activeColour = COLOUR_CATEGORIES.find(c => c.key === category) ?? null;
    const categoryLabel = category
        ? (category === "SUPPLIER" && (supplier || poNumber)
            ? t("po_chip", { supplier: supplier ?? "—", po: poNumber ?? t("pos_no_po") })
            : t(activeColour
                ? activeColour.labelKey
                : category === "MISC" ? "category_misc" : "category_supplier"))
        : null;

    return (
        <div
            className={`scan-panel book-in-scan-panel${isPickStep(phase) ? " book-in-scan-panel--category" : ""}`}
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <ScanFlashOverlay active={!showCloseModal && scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("book_in")}</h1>
                <ScoreDisplay total={score.total} />
                <button className="page-back-btn" onClick={() => navigate("/goods-in")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <ProgressBar progress={timer.state.progress} visible={timer.state.active} />

            {toteId && (
                <div className="book-in-stats-bar">
                    <span className="book-in-stat">
                        <Package size={16} />
                        <strong>{t("tote_label", { tote: toteId })}</strong>
                    </span>
                    {category && (
                        <span className="book-in-stat book-in-stat--category">
                            {activeColour && (
                                <span
                                    className="book-in-category-dot"
                                    style={{ background: activeColour.swatch }}
                                />
                            )}
                            {categoryLabel}
                        </span>
                    )}
                    {poProgress && (
                        <span className="book-in-stat book-in-po-progress">
                            {t("item_po_progress", {
                                received: String(poProgress.received),
                                expected: String(poProgress.expected),
                            })}
                        </span>
                    )}
                    <span className="book-in-stat book-in-stat--count">
                        {t("items_count", { count: String(unitCount(items)) })}
                    </span>
                </div>
            )}

            {/* Step 0 — category picker. Seven tote colours in a grid plus
                the two non-colour categories (Misc, Supplier) on their own
                row. Tapping a tile records the category and advances to the
                scan-tote step. FE-only: nothing is persisted yet. */}
            {phase === "select-category" && (
                <div className="book-in-category-centre">
                    <h2 className="book-in-category-heading">{t("book_in_category_title")}</h2>
                    <p className="book-in-category-sub">{t("book_in_category_sub")}</p>
                    {/* MISC sits as the final tile in the colour grid (next
                        to YELLOW); SUPPLIER stands alone, centred below. */}
                    <div className="book-in-colour-grid">
                        {COLOUR_CATEGORIES.map(c => (
                            <button
                                key={c.key}
                                type="button"
                                className="book-in-colour-tile"
                                style={{ background: c.swatch, color: c.text }}
                                onClick={() => selectCategory(c.key)}
                            >
                                {t(c.labelKey)}
                            </button>
                        ))}
                        <button
                            type="button"
                            className="book-in-category-tile book-in-category-tile--misc"
                            onClick={() => selectCategory("MISC")}
                        >
                            {t("category_misc")}
                        </button>
                    </div>
                    <div className="book-in-category-extra">
                        <button
                            type="button"
                            className="book-in-category-tile book-in-category-tile--supplier"
                            onClick={() => selectCategory("SUPPLIER")}
                        >
                            <Truck size={26} />
                            <span>{t("category_supplier_action")}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Step 0b — supplier list (Choose Supplier tapped). Only
                suppliers switched on in Admin → Warehouse → Suppliers that
                have at least one open PO. */}
            {phase === "select-supplier" && (
                <div className="book-in-category-centre">
                    <h2 className="book-in-category-heading">{t("book_in_select_supplier_title")}</h2>
                    <p className="book-in-category-sub">{t("book_in_select_supplier_sub")}</p>
                    {listLoading ? (
                        <p className="book-in-po-empty">{t("loading")}</p>
                    ) : supplierList.length === 0 ? (
                        <p className="book-in-po-empty">{t("book_in_no_suppliers")}</p>
                    ) : (
                        <div className="book-in-po-list">
                            {supplierList.map(s => (
                                <button
                                    key={s.supplier_name}
                                    type="button"
                                    className="book-in-po-row"
                                    onClick={() => selectSupplier(s.supplier_name)}
                                >
                                    <span className="book-in-po-row-name">{s.supplier_name}</span>
                                    <span className="book-in-po-row-meta">
                                        {t("book_in_supplier_meta", {
                                            pos: String(s.active_pos),
                                            lines: String(s.open_lines),
                                        })}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button type="button" className="book-in-command-action-btn book-in-po-back" onClick={backToCategory}>
                        {t("book_in_back")}
                    </button>
                </div>
            )}

            {/* Step 0c — the supplier's open POs. Tapping one locks it in
                (sticky until Change) and moves to scan-tote. */}
            {phase === "select-po" && (
                <div className="book-in-category-centre">
                    <h2 className="book-in-category-heading">{t("book_in_select_po_title")}</h2>
                    <p className="book-in-category-sub">{t("book_in_select_po_sub", { supplier: supplier ?? "—" })}</p>
                    {listLoading ? (
                        <p className="book-in-po-empty">{t("loading")}</p>
                    ) : poList.length === 0 ? (
                        <p className="book-in-po-empty">{t("book_in_no_pos")}</p>
                    ) : (
                        <div className="book-in-po-list">
                            {poList.map(p => (
                                <button
                                    key={p.po_number ?? "∅"}
                                    type="button"
                                    className="book-in-po-row"
                                    onClick={() => selectPo(p.po_number)}
                                >
                                    <span className="book-in-po-row-name">{p.po_number ?? t("pos_no_po")}</span>
                                    <span className="book-in-po-row-meta">
                                        {t("book_in_po_meta", {
                                            done: String(p.complete_lines),
                                            total: String(p.lines),
                                            received: String(p.received_qty),
                                            expected: String(p.expected_qty),
                                        })}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                    <button
                        type="button"
                        className="book-in-command-action-btn book-in-po-back"
                        onClick={() => { setPoNumber(null); setPhase("select-supplier"); void loadSuppliers(); }}
                    >
                        {t("book_in_back")}
                    </button>
                </div>
            )}

            {!isPickStep(phase) && (
            <div className="scan-centre">
                <div className="scan-command-wrap">
                    <ScanStatusIcons status={showCloseModal || scanStatus === "error" ? "idle" : scanStatus} />
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {commandWords.map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                    {commandSubText && (
                        <div className={`scan-command-sub ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {commandSubText}
                        </div>
                    )}
                    {/* Operator affordances under the command — manual
                        close shortcut for when the physical tote barcode
                        isn't to hand. Shown in tote-open (current tote)
                        and in awaiting-tote with a pending resume (the
                        operator may have lost the tote and just wants
                        to clear it out). Empty routes through the
                        abandon modal; non-empty through close. */}
                    {phase === "tote-open" && !showCloseModal && (
                        <div className="book-in-command-actions">
                            <button
                                type="button"
                                className="book-in-command-action-btn"
                                onClick={() => {
                                    if (items.length === 0) {
                                        setShowAbandonModal(true);
                                    } else {
                                        setShowCloseModal(true);
                                    }
                                    playBoxAdd();
                                }}
                            >
                                {t("close_tote")}
                            </button>
                        </div>
                    )}
                    {/* Chosen-category chip on the scan-tote step, with a
                        Change shortcut back to the picker. Only reachable
                        before a tote is open, so changing it loses nothing. */}
                    {phase === "awaiting-tote" && category && !showCloseModal && (
                        <div className="book-in-command-actions book-in-category-bar">
                            <span className="book-in-category-chip">
                                {activeColour && (
                                    <span
                                        className="book-in-category-dot"
                                        style={{ background: activeColour.swatch }}
                                    />
                                )}
                                {categoryLabel}
                            </span>
                            <button
                                type="button"
                                className="book-in-command-action-btn"
                                onClick={backToCategory}
                            >
                                {t("change_category")}
                            </button>
                        </div>
                    )}
                    {phase === "awaiting-tote" && pendingResume && !showCloseModal && (
                        <div className="book-in-command-actions">
                            <button
                                type="button"
                                className="book-in-command-action-btn"
                                onClick={resumeAndClose}
                            >
                                {t("close_tote")}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Scanned-items list is absolutely positioned over the bottom ~28%
                of the panel. Not rendered on the pick steps: it is empty there
                and its box was swallowing taps on the Choose Supplier tile and
                the supplier / PO lists on a handheld. */}
            {!isPickStep(phase) && (
            <div className="book-in-items-list">
                {/* Numbered by scan order (first scanned = 1), but rendered
                    newest-first so the most recently scanned SKU sits at the
                    top. The number comes from the item's position in `items`
                    (order of first appearance), so re-scanning an existing
                    SKU — which bumps its qty in place — keeps its number and
                    its spot in the list rather than jumping to the top. */}
                {items.map((item, idx) => ({ item, idx, number: idx + 1 }))
                    .reverse()
                    .map(({ item, idx, number }) => {
                    const qty = typeof item.qty === "number" && item.qty > 0 ? item.qty : 1;
                    return (
                        <div key={`${item.barcode}-${item.scannedAt ?? idx}`} className="book-in-item-row">
                            <span className="book-in-item-index">{number}</span>
                            <span className="book-in-item-identity">
                                {/* SKU (from the catalogue) leads in bold; the
                                    scanned barcode trails smaller and lighter.
                                    Legacy items with no resolved SKU fall back
                                    to showing the barcode as the primary text. */}
                                <span className="book-in-item-sku">{item.pkgId ?? item.barcode}</span>
                                {item.pkgId && (
                                    <span className="book-in-item-barcode">{item.barcode}</span>
                                )}
                            </span>
                            {/* Per-row qty editor — only shown when the
                                warehouse has bulk_qty_book_in on. Click
                                opens the modal pre-populated with the
                                current qty so the operator can bump it
                                up after the fact instead of scanning
                                each unit. */}
                            {bulkQty && (
                                <button
                                    type="button"
                                    className={`book-in-item-qty book-in-item-qty--button${qty > 1 ? " book-in-item-qty--multi" : ""}`}
                                    onClick={() => openItemQtyEditor(item)}
                                    title={t("bulk_qty_open")}
                                >
                                    ×{qty}
                                </button>
                            )}
                            <CheckCircle strokeWidth={3} size={18} className="book-in-item-tick" />
                        </div>
                    );
                })}
            </div>
            )}

            {/* No scanner on the pick steps (category / supplier / PO): scans
                are ignored there anyway, and the fixed keyboard / camera
                toggle bar this renders sits over the bottom of the picker on
                a handheld — it was covering the Choose Supplier tile. */}
            {!isPickStep(phase) && (
                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={doSubmit}
                    disabled={completing}
                    onKeyboardToggle={(open) => {
                        const was = keyboardOpenRef.current;
                        keyboardOpenRef.current = open;
                        if (timer.state.active && was !== open) {
                            timer.extend(open ? KEYBOARD_BONUS_MS : -KEYBOARD_BONUS_MS);
                        }
                    }}
                    onSuspectScan={() => { suspectScanRef.current = true; }}
                />
            )}

            {/* Demo — toggled via Admin Settings → Demo Mode */}
            {demo && !isPickStep(phase) && (
                <div className="book-in-demo-panel">
                    <span className="book-in-demo-label">Demo</span>
                    {phase === "awaiting-tote" && (
                        <>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE001")}>TOTE001</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE002")}>TOTE002</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE003")}>TOTE003</button>
                        </>
                    )}
                    {phase === "tote-open" && !showCloseModal && (
                        <>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("11-AGAVE")}>11-AGAVE</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("11-AGIN")}>11-AGIN</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("11-APES")}>11-APES</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("11-ATOMS")}>11-ATOMS</button>
                            <button className="book-in-demo-btn" onClick={() => doSubmit("11-BEADY")}>11-BEADY</button>
                            {toteId && (
                                <button className="book-in-demo-btn book-in-demo-btn--tote" onClick={() => doSubmit(toteId)}>Close {toteId}</button>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Remove-mode toggle — mirrors the keyboard/camera toolbar but on the left */}
            {phase === "tote-open" && !showCloseModal && (
                <div className="book-in-remove-toggle">
                    <button
                        type="button"
                        className={`scan-input-toggle-btn book-in-remove-btn ${removeMode ? "book-in-remove-btn--active" : ""}`}
                        onClick={(e) => {
                            setRemoveMode(v => {
                                const next = !v;
                                // Entering remove mode: halt the timer so the
                                // progress-bar doesn't tick down while the user
                                // is removing items. Leaving is a no-op — the
                                // next valid item scan will restart the timer.
                                if (next) timer.reset();
                                return next;
                            });
                            // Drop focus so the global keydown listener (which
                            // ignores events with a button as target) keeps
                            // capturing hardware scanner keystrokes.
                            (e.currentTarget as HTMLButtonElement).blur();
                        }}
                        aria-label={removeMode ? t("exit_remove_mode") : t("enter_remove_mode")}
                        title={removeMode ? t("exit_remove_mode") : t("enter_remove_mode")}
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            )}

            {showCloseModal && (
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelCloseModal}>
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("close_tote")}</h2>
                            <button className="modal-close" onClick={cancelCloseModal}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{unitCount(items)}</span>
                                    <span className="book-in-close-card-count-label">
                                        {unitCount(items) === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">{t("close_tote_confirm")}</p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={() => completeTote()}
                                    disabled={completing || items.length === 0}
                                >
                                    {t("complete")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={() => completeTote({ thenPutaway: true })}
                                    disabled={completing || items.length === 0}
                                >
                                    {completing ? t("submitting") : t("complete_and_putaway")}
                                </button>
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelCloseModal}
                                    disabled={completing}
                                >
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Per-row qty editor — opened by clicking the qty chip on a
                book_in row. Updates that single row's qty server-side;
                completeTote later multiplies SKU consumption by qty. */}
            {editingItem && (
                <div className="modal-overlay book-in-modal-overlay" onClick={() => !editingQtySubmitting && closeItemQtyEditor()}>
                    <div className="modal-card book-in-qty-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("bulk_qty_title")}</h2>
                            <button className="modal-close" onClick={closeItemQtyEditor} disabled={editingQtySubmitting}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-qty-identity">
                                <span className="book-in-qty-sku">{editingItem.pkgId ?? editingItem.barcode}</span>
                                {editingItem.pkgId && (
                                    <span className="book-in-qty-barcode">{editingItem.barcode}</span>
                                )}
                                {editingItem.title && (
                                    <span className="book-in-qty-title">{editingItem.title}</span>
                                )}
                            </div>
                            <label className="book-in-qty-label">{t("bulk_qty_qty_label")}</label>
                            <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={9999}
                                step={1}
                                className="book-in-qty-input"
                                value={editingQtyValue}
                                autoFocus
                                onFocus={e => e.currentTarget.select()}
                                onChange={e => setEditingQtyValue(e.target.value.replace(/[^0-9]/g, ""))}
                                onKeyDown={e => {
                                    if (e.key === "Enter") { e.preventDefault(); submitItemQtyEdit(); }
                                    if (e.key === "Escape") { e.preventDefault(); closeItemQtyEditor(); }
                                }}
                                disabled={editingQtySubmitting}
                            />
                            <div className="modal-confirm-buttons book-in-qty-actions">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={closeItemQtyEditor}
                                    disabled={editingQtySubmitting}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={submitItemQtyEdit}
                                    disabled={editingQtySubmitting}
                                >
                                    {editingQtySubmitting ? t("submitting") : t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAbandonModal && (
                <div className="modal-overlay book-in-modal-overlay" onClick={() => !abandoning && setShowAbandonModal(false)}>
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("abandon_tote_title")}</h2>
                            <button className="modal-close" onClick={() => setShowAbandonModal(false)} disabled={abandoning}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">0</span>
                                    <span className="book-in-close-card-count-label">
                                        {t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint book-in-close-hint--danger">
                                {t("abandon_tote_body")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => setShowAbandonModal(false)}
                                    disabled={abandoning}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={abandonTote}
                                    disabled={abandoning}
                                >
                                    {abandoning ? t("submitting") : t("abandon_tote_yes")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Off-PO scan: the SKU isn't on the chosen PO. Add creates a
                PO line for it at close; Cancel drops the scan. */}
            {notOnPoPrompt && (
                <div className="modal-overlay book-in-modal-overlay" onClick={() => !addingOffPo && cancelNotOnPo()}>
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("item_not_on_po_title")}</h2>
                            <button className="modal-close" onClick={cancelNotOnPo} disabled={addingOffPo}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="book-in-close-hint">
                                {t("item_not_on_po_body", {
                                    sku: notOnPoPrompt.pkgId ?? notOnPoPrompt.barcode,
                                    po: poNumber ?? t("pos_no_po"),
                                })}
                            </p>
                            {notOnPoPrompt.title && (
                                <p className="book-in-close-hint">{notOnPoPrompt.title}</p>
                            )}
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelNotOnPo}
                                    disabled={addingOffPo}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={confirmAddOffPo}
                                    disabled={addingOffPo}
                                >
                                    {addingOffPo ? t("submitting") : t("item_not_on_po_add")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {joinPrompt && (
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelJoin}>
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("join_tote_title")}</h2>
                            <button className="modal-close" onClick={cancelJoin}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{joinPrompt.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{unitCount(joinPrompt.items)}</span>
                                    <span className="book-in-close-card-count-label">
                                        {unitCount(joinPrompt.items) === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {joinPrompt.startedByName
                                    ? t("join_tote_intro_named", { name: joinPrompt.startedByName })
                                    : t("join_tote_intro")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelJoin}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={acceptJoin}
                                >
                                    {t("join_tote_join")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {reopenPrompt && (
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelReopen}>
                    <div className="modal-card book-in-reopen-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{confirmingEmpty ? t("reopen_empty_confirm_title") : t("reopen_tote_title")}</h2>
                            <button className="modal-close" onClick={cancelReopen} disabled={reopening}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{reopenPrompt.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{unitCount(reopenPrompt.items)}</span>
                                    <span className="book-in-close-card-count-label">
                                        {unitCount(reopenPrompt.items) === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            {confirmingEmpty ? (
                                <>
                                    <p className="book-in-close-hint book-in-close-hint--danger">
                                        {t("reopen_empty_confirm_body")}
                                    </p>
                                    <div className="modal-confirm-buttons">
                                        <button
                                            className="btn btn-ghost btn-lg"
                                            onClick={() => setConfirmingEmpty(false)}
                                            disabled={reopening}
                                        >
                                            {t("cancel")}
                                        </button>
                                        <button
                                            className="btn btn-danger btn-lg"
                                            onClick={() => handleReopenChoice("empty")}
                                            disabled={reopening}
                                        >
                                            {t("reopen_empty_confirm_yes")}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="book-in-close-hint">
                                        {reopenPrompt.completedByName
                                            ? t("reopen_tote_intro_named", { name: reopenPrompt.completedByName })
                                            : t("reopen_tote_intro")}
                                    </p>
                                    <div className="modal-confirm-buttons book-in-reopen-buttons">
                                        <button
                                            className="btn btn-ghost btn-lg"
                                            onClick={cancelReopen}
                                            disabled={reopening}
                                        >
                                            {t("cancel")}
                                        </button>
                                        <button
                                            className="btn btn-danger btn-lg"
                                            onClick={() => setConfirmingEmpty(true)}
                                            disabled={reopening}
                                        >
                                            {t("reopen_empty")}
                                        </button>
                                        <button
                                            className="btn btn-success btn-lg"
                                            onClick={() => handleReopenChoice("continue")}
                                            disabled={reopening}
                                        >
                                            {t("reopen_continue")}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookIn;
